/**
 * Minimal fetch wrapper for the Blossom backend.
 *
 * Deliberately dependency-free: the counter PC runs this all day, so the less
 * that can go wrong the better. Everything the app needs from an HTTP layer is
 * here — base URL resolution, the bearer token, the `{ success, data }`
 * envelope, and errors that carry the server's message through to the counter staff.
 */

const DEV_DEFAULT = 'http://localhost:8000/api/v1';
const PROD_DEFAULT = 'https://api.blossombookfair.com/api/v1';

function resolveBaseUrl(): string {
  const fromEnv = import.meta.env.VITE_API_URL;
  if (fromEnv && fromEnv.trim()) return fromEnv.trim().replace(/\/$/, '');
  return import.meta.env.DEV ? DEV_DEFAULT : PROD_DEFAULT;
}

export const BASE_URL = resolveBaseUrl();

/** How long a single request may take before we call it dead. */
const TIMEOUT_MS = 20_000;

export class ApiError extends Error {
  /** HTTP status, or 0 when the request never reached the server. */
  readonly status: number;
  readonly payload: unknown;

  constructor(status: number, message: string, payload?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
  }

  /** True when the browser could not talk to the API at all. */
  get isNetworkError(): boolean {
    return this.status === 0;
  }

  /**
   * True when the server turned the request away because the till is switched
   * off for maintenance. Nothing was done — a sale that gets this was NOT
   * banked, and has to be completed again once the till is back.
   */
  get isMaintenance(): boolean {
    return isMaintenancePayload(this.status, this.payload);
  }
}

// --- maintenance ------------------------------------------------------------
// While an admin has the till switched off, every call answers 503 with
// `code: 'MAINTENANCE'`. The request layer only reports it; the maintenance
// screen owns what happens next, and registers itself here the same way the
// AuthContext registers for an expired session.

export interface MaintenanceNotice {
  message: string | null;
  endsAt: string | null;
}

let onMaintenance: ((notice: MaintenanceNotice) => void) | null = null;

export function setOnMaintenance(cb: ((notice: MaintenanceNotice) => void) | null) {
  onMaintenance = cb;
}

function isMaintenancePayload(status: number, payload: unknown): boolean {
  return (
    status === 503 &&
    !!payload &&
    typeof payload === 'object' &&
    (payload as Record<string, unknown>).code === 'MAINTENANCE'
  );
}

/** Tell the maintenance screen, if this failure is the till being switched off. */
function reportMaintenance(status: number, payload: unknown) {
  if (!isMaintenancePayload(status, payload)) return;
  const body = payload as Record<string, unknown>;
  try {
    onMaintenance?.({
      message: typeof body.message === 'string' ? body.message : null,
      endsAt: typeof body.endsAt === 'string' ? body.endsAt : null,
    });
  } catch {
    /* the screen must never be the reason a request blows up */
  }
}

// --- token plumbing ---------------------------------------------------------
// The AuthContext owns the token; it pushes the current value down here so the
// request layer stays a plain module (no hooks, importable from anywhere).

let accessToken: string | null = null;
let refreshToken: string | null = null;
let onAuthExpired: (() => void) | null = null;

export function setTokens(tokens: { accessToken: string | null; refreshToken: string | null }) {
  accessToken = tokens.accessToken;
  refreshToken = tokens.refreshToken;
}

export function getRefreshToken(): string | null {
  return refreshToken;
}

export function setOnAuthExpired(cb: (() => void) | null) {
  onAuthExpired = cb;
}

// --- core request -----------------------------------------------------------

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Skip the 401 -> refresh -> retry dance (used by the refresh call itself). */
  skipRefresh?: boolean;
  signal?: AbortSignal;
  /** For the rare call that is slow by nature. Defaults to TIMEOUT_MS. */
  timeoutMs?: number;
}

function extractMessage(payload: unknown, fallback: string): string {
  if (payload && typeof payload === 'object') {
    const msg = (payload as Record<string, unknown>).message;
    if (Array.isArray(msg)) return msg.filter(Boolean).join(', ');
    if (typeof msg === 'string' && msg.trim()) return msg;
    const err = (payload as Record<string, unknown>).error;
    if (typeof err === 'string' && err.trim()) return err;
  }
  return fallback;
}

async function rawRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal, timeoutMs = TIMEOUT_MS } = options;

  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  // Let callers cancel too (the search box aborts superseded lookups).
  const onExternalAbort = () => controller.abort();
  signal?.addEventListener('abort', onExternalAbort);

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: controller.signal,
    });
  } catch (err) {
    if (signal?.aborted) throw err; // caller cancelled on purpose — bubble the AbortError
    throw new ApiError(
      0,
      'Cannot reach the server. Check the counter’s network connection and try again.',
      err,
    );
  } finally {
    window.clearTimeout(timer);
    signal?.removeEventListener('abort', onExternalAbort);
  }

  const text = await response.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text) as unknown;
    } catch {
      payload = text;
    }
  }

  if (!response.ok) {
    reportMaintenance(response.status, payload);
    throw new ApiError(
      response.status,
      extractMessage(payload, `Request failed (${response.status})`),
      payload,
    );
  }

  // Unwrap the backend's { success, data } envelope when it is present.
  if (payload && typeof payload === 'object' && 'data' in (payload as object) && 'success' in (payload as object)) {
    return (payload as { data: T }).data;
  }
  return payload as T;
}

// --- 401 handling -----------------------------------------------------------
// One refresh attempt, shared across concurrent 401s. If it fails the session is
// over and the app drops back to the login screen.

let refreshInFlight: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  if (!refreshToken) throw new ApiError(401, 'Session expired. Please sign in again.');

  refreshInFlight ??= rawRequest<{ accessToken: string; refreshToken?: string }>('/auth/refresh', {
    method: 'POST',
    body: { refreshToken },
    skipRefresh: true,
  })
    .then((tokens) => {
      accessToken = tokens.accessToken;
      if (tokens.refreshToken) refreshToken = tokens.refreshToken;
      return tokens.accessToken;
    })
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  try {
    return await rawRequest<T>(path, options);
  } catch (err) {
    const is401 = err instanceof ApiError && err.status === 401;
    if (!is401 || options.skipRefresh) throw err;

    // No refresh token means nobody is signed in, so there is no session and
    // nothing has expired: this 401 is the endpoint's own answer — a wrong
    // password at the login screen, most of the time. Refreshing anyway would
    // fail, fire onAuthExpired at a screen that is already the login screen,
    // and replace the server's "invalid username or password" with "your
    // session expired", which is both untrue and useless to the person typing.
    if (!refreshToken) throw err;

    try {
      await refreshAccessToken();
    } catch (refreshErr) {
      // The refresh call itself hit maintenance: the session is fine, the
      // server is just closed. Signing out here would throw away the bill on
      // the till for nothing.
      if (refreshErr instanceof ApiError && refreshErr.isMaintenance) throw refreshErr;
      onAuthExpired?.();
      throw new ApiError(401, 'Your session expired. Please sign in again.');
    }
    try {
      return await rawRequest<T>(path, options);
    } catch (retryErr) {
      if (retryErr instanceof ApiError && retryErr.status === 401) onAuthExpired?.();
      throw retryErr;
    }
  }
}

/**
 * Fetch a binary response (the receipt PDF).
 *
 * Separate from `request` because that one parses JSON and unwraps the
 * envelope, both of which would corrupt a PDF. The token has to travel in a
 * header, so the file cannot simply be opened as a URL in a new tab — it is
 * fetched here and handed to the browser as a blob.
 *
 * A 401 refreshes once and retries, matching the JSON path: a staff member midway
 * through a queue should never be bounced to the login screen just because
 * their access token aged out between the sale and the print.
 */
async function rawBlob(path: string): Promise<Blob> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const response = await fetch(`${BASE_URL}${path}`, {
      headers: { ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}) },
      signal: controller.signal,
    });
    if (!response.ok) {
      let payload: unknown = null;
      try {
        payload = JSON.parse(await response.text()) as unknown;
      } catch {
        /* not JSON */
      }
      reportMaintenance(response.status, payload);
      throw new ApiError(
        response.status,
        extractMessage(payload, `Could not fetch the receipt (${response.status})`),
        payload,
      );
    }
    return await response.blob();
  } catch (err) {
    if (err instanceof ApiError) throw err;
    throw new ApiError(0, 'Cannot reach the server to print the receipt.', err);
  } finally {
    window.clearTimeout(timer);
  }
}

export async function requestBlob(path: string): Promise<Blob> {
  try {
    return await rawBlob(path);
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 401) throw err;
    try {
      await refreshAccessToken();
    } catch (refreshErr) {
      if (refreshErr instanceof ApiError && refreshErr.isMaintenance) throw refreshErr;
      onAuthExpired?.();
      throw new ApiError(401, 'Your session expired. Please sign in again.');
    }
    return rawBlob(path);
  }
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { method: 'GET', signal }),
  post: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(path, { method: 'POST', body, signal }),
  /**
   * For endpoints that are reached without a session — signing in, and nothing
   * else today. Skips the refresh-and-retry entirely rather than relying on
   * there being no token to notice, so a 401 from these comes back exactly as
   * the server wrote it.
   */
  postPublic: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body, skipRefresh: true }),
  patch: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(path, { method: 'PATCH', body, signal }),
  blob: (path: string) => requestBlob(path),
};

/**
 * POST a file as multipart/form-data. Kept apart from `request`, which sets a
 * JSON content type — a multipart body needs the browser to write its own
 * boundary. A 401 refreshes the session once and retries, exactly as `request`
 * does, so an access token that aged out does not turn into "Unauthorized".
 */
async function rawUpload<T>(path: string, file: File): Promise<T> {
  const form = new FormData();
  form.append('file', file);
  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method: 'POST',
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      body: form,
    });
  } catch (err) {
    throw new ApiError(0, 'Cannot reach the server. Check the network connection and try again.', err);
  }
  const text = await response.text();
  let payload: unknown = null;
  try {
    payload = text ? (JSON.parse(text) as unknown) : null;
  } catch {
    payload = text;
  }
  if (!response.ok) {
    reportMaintenance(response.status, payload);
    throw new ApiError(
      response.status,
      extractMessage(payload, `Upload failed (${response.status})`),
      payload,
    );
  }
  if (payload && typeof payload === 'object' && 'data' in payload && 'success' in payload) {
    return (payload as { data: T }).data;
  }
  return payload as T;
}

export async function uploadFile<T>(path: string, file: File): Promise<T> {
  try {
    return await rawUpload<T>(path, file);
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 401 || !refreshToken) throw err;
    try {
      await refreshAccessToken();
    } catch (refreshErr) {
      if (refreshErr instanceof ApiError && refreshErr.isMaintenance) throw refreshErr;
      onAuthExpired?.();
      throw new ApiError(401, 'Your session expired. Please sign in again.');
    }
    return rawUpload<T>(path, file);
  }
}

/** Human-readable message for any thrown value. */
export function errorMessage(err: unknown, fallback = 'Something went wrong'): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}
